/**
 * AdminReviewsTab - Review management tab for admin panel
 * Self-contained component for managing club reviews
 * Extracted from AdminPage.js for better maintainability
 */
import React, { useState, useEffect } from 'react';
import { 
  Trash2, Star, Pencil, Plus, Loader2, ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

import { API_URL } from '../../utils/api';

export function AdminReviewsTab({ onDataChange }) {
  // State
  const [reviews, setReviews] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clubsLoading, setClubsLoading] = useState(true);
  
  // Modal state
  const [showAddReviewModal, setShowAddReviewModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewFormData, setReviewFormData] = useState({
    club_id: '',
    reviewer_name: '',
    rating: 5,
    comment: '',
    source: ''
  });
  const [saving, setSaving] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchReviews();
    fetchClubs();
  }, []);

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    setClubsLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching clubs with token:', token ? 'present' : 'missing');
      const response = await fetch(`${API_URL}/api/admin/clubs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Clubs response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Clubs loaded:', data.length, data);
        setClubs(data);
      } else {
        console.error('Failed to fetch clubs:', response.status);
        toast.error('Failed to load clubs');
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
      toast.error('Error loading clubs');
    } finally {
      setClubsLoading(false);
    }
  };

  const openAddReviewModal = () => {
    setEditingReview(null);
    setReviewFormData({
      club_id: '',
      reviewer_name: '',
      rating: 5,
      comment: '',
      source: ''
    });
    setShowAddReviewModal(true);
  };

  const openEditReviewModal = (review) => {
    setEditingReview(review);
    setReviewFormData({
      club_id: review.club_id || '',
      reviewer_name: review.reviewer_name || '',
      rating: review.rating || 5,
      comment: review.comment || '',
      source: review.source || ''
    });
    setShowAddReviewModal(true);
  };

  const handleSaveReview = async () => {
    if (!reviewFormData.club_id || !reviewFormData.reviewer_name || !reviewFormData.comment) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingReview 
        ? `${API_URL}/api/admin/reviews/${editingReview.review_id}`
        : `${API_URL}/api/admin/reviews`;
      
      const response = await fetch(url, {
        method: editingReview ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reviewFormData)
      });

      if (response.ok) {
        toast.success(editingReview ? 'Review updated' : 'Review added');
        setShowAddReviewModal(false);
        fetchReviews();
        if (onDataChange) onDataChange();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save review');
      }
    } catch (error) {
      toast.error('Error saving review');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Review deleted');
        fetchReviews();
        if (onDataChange) onDataChange();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete review');
      }
    } catch (error) {
      toast.error('Error deleting review');
    }
  };

  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} 
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">All Reviews</h3>
          <Button 
            onClick={openAddReviewModal}
            className="bg-violet-600 hover:bg-violet-500"
            data-testid="add-review-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add External Review
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Review</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Club</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Rating</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Source</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map(review => (
                <tr key={review.review_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-slate-900">{review.reviewer_name}</p>
                      <p className="text-sm text-slate-500 line-clamp-2 max-w-md">{review.comment}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {review.club_name}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex">{renderStars(review.rating)}</div>
                  </td>
                  <td className="px-6 py-4">
                    {review.source ? (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        {review.source}
                      </Badge>
                    ) : (
                      <span className="text-sm text-slate-500">Direct</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {new Date(review.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-violet-600 hover:text-violet-700" 
                        onClick={() => openEditReviewModal(review)}
                        data-testid={`edit-review-${review.review_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid={`delete-review-${review.review_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Review</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this review by {review.reviewer_name}?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteReview(review.review_id)} className="bg-red-600 hover:bg-red-700">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {reviews.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No reviews found
          </div>
        )}
      </div>

      {/* Add/Edit Review Modal */}
      <Dialog open={showAddReviewModal} onOpenChange={setShowAddReviewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingReview ? 'Edit Review' : 'Add External Review'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Club *</label>
              {clubsLoading ? (
                <div className="flex items-center gap-2 h-9 px-3 border border-slate-300 rounded-md bg-slate-50">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  <span className="text-sm text-slate-500">Loading clubs...</span>
                </div>
              ) : clubs.length === 0 ? (
                <div className="flex items-center gap-2 h-9 px-3 border border-red-300 rounded-md bg-red-50">
                  <span className="text-sm text-red-600">No clubs found. Please create a club first.</span>
                </div>
              ) : (
                <Select 
                  value={reviewFormData.club_id} 
                  onValueChange={(val) => setReviewFormData(prev => ({ ...prev, club_id: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a club" />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {clubs.map(club => (
                      <SelectItem key={club.club_id} value={club.club_id}>{club.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Reviewer Name *</label>
              <Input
                value={reviewFormData.reviewer_name}
                onChange={(e) => setReviewFormData(prev => ({ ...prev, reviewer_name: e.target.value }))}
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Rating</label>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setReviewFormData(prev => ({ ...prev, rating }))}
                    className="p-1"
                  >
                    <Star 
                      className={`w-6 h-6 ${rating <= reviewFormData.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Comment *</label>
              <Textarea
                value={reviewFormData.comment}
                onChange={(e) => setReviewFormData(prev => ({ ...prev, comment: e.target.value }))}
                rows={3}
                placeholder="Write the review..."
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Source (optional)</label>
              <Input
                value={reviewFormData.source}
                onChange={(e) => setReviewFormData(prev => ({ ...prev, source: e.target.value }))}
                placeholder="e.g., Google, Facebook"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddReviewModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveReview} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingReview ? 'Update Review' : 'Add Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AdminReviewsTab;
